import { customAuth, oauthAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import { domainToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

// FedRAMP tenants are isolated environments with their own Zidentity and
// gateway, so they follow neither the zslogin{cloud}.net nor the
// api.{cloud}.zsapi.net pattern.
const FEDRAMP_CLOUDS: Record<
  string,
  { loginDomain: string; apiBaseUrl: string }
> = {
  gov: {
    loginDomain: "zidentitygov.net",
    apiBaseUrl: "https://api.zscalergov.net",
  },
  govus: {
    loginDomain: "zidentitygov.us",
    apiBaseUrl: "https://api.zscalergov.us",
  },
};

const normalizedCloud = (cloud: string | undefined) =>
  cloud?.trim().toLowerCase() ?? "";

// Users enter the legacy cloud both bare ("zscalertwo") and as a domain
// ("zscalertwo.net"); the proxy's session login normalizes the same way.
const legacyCloudName = (cloud: string) =>
  normalizedCloud(cloud).replace(/\.net$/, "");

const oneApiBaseUrl = ({ cloud }: { cloud?: string }) => {
  const segment = normalizedCloud(cloud);
  const fedramp = FEDRAMP_CLOUDS[segment];
  if (fedramp) return fedramp.apiBaseUrl;
  return segment ? `https://api.${segment}.zsapi.net` : "https://api.zsapi.net";
};

const oneApiTokenEndpoint = ({
  vanityDomain,
  cloud,
}: {
  vanityDomain: string;
  cloud?: string;
}) => {
  const segment = normalizedCloud(cloud);
  const loginDomain =
    FEDRAMP_CLOUDS[segment]?.loginDomain ?? `zslogin${segment}.net`;
  return `https://${vanityDomain}.${loginDomain}/oauth2/v1/token`;
};

export const zscaler = defineProduct({
  name: "Zscaler",
  tagLine: "Cloud security platform",
  logo,
  tags: ["security"],
  variants: [
    productVariant({
      identifier: "zscaler",
      authLabel: "Legacy ZIA API key",
      protocol: "http",
      defaultUrlPattern: () =>
        "https://.*\\.(zscaler[a-z]*|zscloud|zspreview)\\.net/.*",
      auth: customAuth("zscaler"),
      steps: [
        {
          title: "Get your Zscaler API credentials",
          items: [
            {
              kind: "markdown",
              content:
                "In the Zscaler Admin Portal, go to **Administration > API Key Management** to create or retrieve your API key and credentials.",
            },
          ],
        },
        {
          title: "Enter your Zscaler details",
          items: [
            {
              kind: "input",
              label: "Cloud name",
              required: true,
              placeholder: "zscaler",
              target: { kind: "configuration", field: "cloud" },
              helperText:
                "Your Zscaler cloud (e.g., zscaler, zscalerone, zscalertwo).",
            },
            {
              kind: "input",
              label: "API key",
              required: true,
              target: { kind: "secrets", field: "apiKey" },
            },
            {
              kind: "input",
              label: "Username",
              required: true,
              target: { kind: "configuration", field: "username" },
            },
            {
              kind: "input",
              label: "Password",
              required: true,
              target: { kind: "secrets", field: "password" },
            },
          ],
        },
      ],
      connectionTest: {
        protocol: "http",
        request: ({ cloud }) =>
          `https://zsapi.${legacyCloudName(cloud)}.net/api/v1/status`,
      },
    }),
    productVariant({
      identifier: "zscalerOneApi",
      authLabel: "OneAPI OAuth client",
      protocol: "http",
      defaultUrlPattern: (configuration) =>
        domainToRegex(oneApiBaseUrl(configuration)),
      auth: oauthAuth,
      steps: [
        {
          title: "Create an API client in Zidentity",
          items: [
            {
              kind: "markdown",
              content:
                "In the Zidentity Admin Portal, go to **Integration > API Clients** and add an API client with the resources and scopes you need, then copy the client ID and client secret. See Zscaler’s [OneAPI getting started guide](https://help.zscaler.com/oneapi/getting-started).",
            },
          ],
        },
        {
          title: "Enter your Zidentity details",
          items: [
            {
              kind: "input",
              label: "Vanity domain",
              required: true,
              placeholder: "your-company",
              target: { kind: "configuration", field: "vanityDomain" },
              helperText:
                "The subdomain of your Zidentity login URL (your-company.zslogin.net).",
            },
            {
              kind: "input",
              label: "Cloud name",
              placeholder: "beta",
              target: { kind: "configuration", field: "cloud" },
              helperText:
                "Leave blank for production. Use beta for api.beta.zsapi.net, or gov or govus for a FedRAMP tenant.",
            },
          ],
        },
        {
          title: "Enter your credentials",
          items: [
            {
              kind: "input",
              label: "Client ID",
              required: true,
              target: { kind: "oauthConnection", field: "clientId" },
            },
            {
              kind: "input",
              label: "Client secret",
              required: true,
              target: { kind: "oauthConnection", field: "clientSecret" },
            },
          ],
        },
      ],
      oauth: {
        grantType: () => "client_credentials",
        tokenEndpoint: oneApiTokenEndpoint,
        clientCredentialsFrom: "db",
        tokenEndpointAuthMethod: () => "client_secret_post",
        // Zidentity issues OneAPI tokens only for this fixed audience on every
        // cloud, and derives their scopes from the API client’s assigned
        // resources rather than from the token request.
        authorizationParameters: () => ({
          audience: "https://api.zscaler.com",
        }),
        scopes: () => [],
      },
      connectionTest: {
        protocol: "http",
        request: (configuration) => ({
          url: `${oneApiBaseUrl(configuration)}/zia/api/v1/status`,
          // No OneAPI path is product-agnostic — the gateway routes purely by
          // product prefix — so an API client scoped to ZPA or ZDX alone is
          // correctly connected yet forbidden here.
          expectedStatus: [200, 403],
        }),
      },
      publicEnvVars: (configuration) => ({
        ZSCALER_ONEAPI_URL: oneApiBaseUrl(configuration),
      }),
      usageHint: (configuration) =>
        `The OneAPI base URL is ${oneApiBaseUrl(configuration)}, with product paths beneath it (e.g. /zia/api/v1/…, /zpa/…). Authentication is injected automatically, so never fetch tokens yourself.`,
    }),
  ],
});
